import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Globe, Facebook, Instagram, Shirt, X, Truck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RibbonMarquee } from "@/components/Marquee";
import { RevealLine, FadeUp } from "@/components/Kinetic";
import { PantherWordmark } from "@/components/Logo";
import { QuoteForm } from "@/components/QuoteForm";
import { PANTHER_LINKS } from "@/lib/api";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const IMG = {
  hero: "https://images.unsplash.com/photo-1758119354171-f14f082c5e02?auto=format&fit=crop&w=1200&q=80",
};

const KIT_GUIDES = {
  kids: {
    src: "/img/panther-tailles-kids.webp",
    alt: "Guide des tailles PANTHER Kids — de 2 à 12 ans",
    label: "Enfant (2–12 ans)",
  },
  adult: {
    src: "/img/panther-tailles-adulte.webp",
    alt: "Guide des tailles PANTHER Adulte — de XS à 5XL",
    label: "Adulte (XS–5XL)",
  },
};

const STATS = [
  { value: "+50", label: "ans d'expertise Tremblay" },
  { value: "5 000 m²", label: "de stock permanent" },
  { value: "2 M", label: "d'articles livrés chaque année" },
  { value: "+1 500", label: "références au catalogue" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="home-hero">
      <div className="absolute inset-0 spotlight-blaze" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 md:px-10 pt-28 pb-20 grid lg:grid-cols-12 gap-14 items-center w-full relative z-10">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <RevealLine delay={0.15}>
            <span className="font-accent uppercase tracking-[0.4em] text-blaze text-sm md:text-base">
              Distributeur d'équipements sportifs
            </span>
          </RevealLine>
          <h1 className="mt-6 font-display leading-[0.85] tracking-tight text-[clamp(3.2rem,10vw,8.5rem)]">
            <RevealLine delay={0.3}>LIGÉRIA</RevealLine>
            <RevealLine delay={0.42}><span className="text-outline">SPORT</span></RevealLine>
            <RevealLine delay={0.54}><span className="text-blaze">CONCEPT</span></RevealLine>
          </h1>
          <RevealLine delay={0.75}>
            <p className="mt-8 max-w-xl text-white/60 text-base md:text-lg leading-relaxed font-light">
              Deux marques, un seul terrain de jeu : le catalogue complet Tremblay
              pour vos clubs et collectivités, et la marque PANTHER.
              Repérez vos références, envoyez votre demande.
            </p>
          </RevealLine>
          <FadeUp delay={0.9} className="mt-10 flex flex-wrap items-stretch gap-5">
            <div className="flex items-center gap-4 bg-white/5 border border-white/10 p-3">
              <img
                src="/img/logo-tremblay.png"
                alt="Logo Tremblay"
                className="h-11 w-auto rounded-md"
                data-testid="hero-logo-tremblay"
              />
              <Link
                to="/catalogue"
                data-testid="hero-cta-catalogue"
                className="group px-8 py-4 bg-blaze text-white font-accent uppercase tracking-[0.22em] text-sm flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_rgba(200,16,46,0.4)] transition-all duration-200"
              >
                Explorer le catalogue Tremblay
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <a
              href="#panther"
              data-testid="hero-cta-panther"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("panther");
                if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
                else el?.scrollIntoView({ behavior: "smooth" });
              }}
              aria-label="Découvrir PANTHER"
              title="Découvrir PANTHER"
              className="self-stretch px-7 flex items-center border border-white/15 hover:border-blaze/60 transition-colors duration-200"
            >
              <img src="/img/panther-band.png" alt="Découvrir PANTHER" className="h-11 w-auto" />
            </a>
          </FadeUp>
        </motion.div>

        <motion.div style={{ y }} className="hidden lg:block lg:col-span-5 relative" data-testid="hero-image">
          <div className="absolute -inset-10 spotlight-blaze" aria-hidden="true" />
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative clip-frame"
          >
            <img
              src={IMG.hero}
              alt="Saut de handball en match nocturne"
              className="w-full h-[620px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10 bg-black/40 backdrop-blur-sm">
              <p className="font-accent uppercase tracking-[0.3em] text-white/70 text-sm">
                Partenaire de toutes vos victoires
              </p>
            </div>
          </motion.div>
          <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-blaze" aria-hidden="true" />
        </motion.div>
      </div>
    </section>
  );
};

const TremblayBrique = () => (
  <section id="tremblay" className="relative py-28 md:py-36" data-testid="tremblay-section">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <FadeUp className="flex items-center gap-6 mb-14">
        <span className="font-display text-blaze text-2xl">01</span>
        <span className="h-px flex-1 bg-white/10" />
        <span className="font-accent uppercase tracking-[0.4em] text-white/50 text-sm">La brique Tremblay</span>
      </FadeUp>

      <div className="grid lg:grid-cols-12 gap-5">
        <FadeUp className="lg:col-span-7 lg:row-span-2 bg-panel border border-white/10 p-10 md:p-14 flex flex-col justify-between min-h-[420px] hover:border-white/25 transition-colors duration-300">
          <div>
            <img
              src="/img/logo-tremblay.png"
              alt="Logo Tremblay"
              className="h-14 md:h-16 w-auto rounded-lg"
              data-testid="tremblay-logo-brique"
            />
            <h2 className="mt-8 font-display leading-[0.9] tracking-tight text-4xl md:text-6xl uppercase">
              L'équipement des clubs
              <br />
              <span className="text-blaze">et des collectivités</span>
            </h2>
            <p className="mt-8 text-white/60 leading-relaxed max-w-lg font-light">
              Depuis plus de 50 ans, Tremblay propose des produits fiables et adaptés
              aux besoins des clubs et des collectivités — du coup d'envoi jusqu'au
              coup de sifflet final. Retrouvez la reproduction fidèle du catalogue
              2026 : 188 pages, navigation par rayon et références.
            </p>
          </div>
          <Link
            to="/catalogue"
            data-testid="tremblay-open-catalogue"
            className="group mt-10 w-fit px-8 py-4 bg-white text-ink font-accent uppercase tracking-[0.22em] text-sm flex items-center gap-3 hover:bg-blaze hover:text-white transition-colors duration-200"
          >
            Accéder au catalogue
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-5 relative overflow-hidden group min-h-[300px] bg-white border border-white/10">
          <img
            src="/catalog/products/p039_1211_1.jpg"
            alt="Ballon de football stabilisé Tremblay"
            className="absolute inset-0 w-full h-full object-contain p-8 group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-black/80 backdrop-blur-sm border-t border-white/10">
            <p className="font-accent uppercase tracking-[0.25em] text-white/70 text-xs">Ballon de football Tremblay — Catalogue 2026</p>
          </div>
        </FadeUp>

        <FadeUp delay={0.2} className="lg:col-span-5 grid grid-cols-2 gap-5">
          {STATS.map((s, i) => (
            <div key={i} className="bg-panel border border-white/10 p-6 flex flex-col justify-between min-h-[150px] hover:border-white/25 transition-colors duration-300">
              <span className="font-display text-3xl md:text-4xl text-white">{s.value}</span>
              <span className="font-accent uppercase tracking-[0.18em] text-xs text-white/50 mt-4">{s.label}</span>
            </div>
          ))}
        </FadeUp>

        <FadeUp delay={0.15} className="lg:col-span-12 relative overflow-hidden group border border-white/10">
          <div className="flex h-56 md:h-72">
            {[
              { src: "/catalog/products/minibut.jpg", alt: "Mini-but — catalogue Tremblay 2026" },
              { src: "/catalog/products/chasuble.jpg", alt: "Chasuble — catalogue Tremblay 2026" },
              { src: "/catalog/products/soin.jpg", alt: "Trousse de soin — catalogue Tremblay 2026" },
              { src: "/catalog/products/tennis_table.jpg", alt: "Tennis de table — catalogue Tremblay 2026" },
            ].map((p, i) => (
              <img
                key={i}
                src={p.src}
                alt={p.alt}
                className="flex-1 min-w-0 h-full object-contain bg-white p-4 group-hover:scale-[1.03] transition-transform duration-700"
              />
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-black/50 to-transparent" />
          <div className="absolute inset-0 flex items-center px-8 md:px-14">
            <div>
              <p className="font-display uppercase text-2xl md:text-4xl max-w-md leading-tight text-white flex items-center gap-4">
                <Truck className="h-8 md:h-10 text-blaze shrink-0" aria-hidden="true" />
                <span>Livraison offerte dès 370 € HT</span>
              </p>
              <p className="mt-3 font-accent uppercase tracking-[0.25em] text-white/50 text-xs">
                En dessous, frais de port sur devis — livraison sous 4 jours
              </p>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const PantherBrique = () => {
  const [kit, setKit] = useState(null);
  return (
  <section id="panther" className="relative bg-chalk text-black overflow-hidden" data-testid="panther-section">
    <div className="h-1.5 flex" aria-hidden="true">
      <div className="flex-1 bg-black" />
      <div className="flex-1 bg-royal" />
      <div className="flex-1 bg-blaze" />
    </div>
    <img
      src="/img/panther-head-black.png"
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 right-0 h-full w-full object-cover object-right opacity-[0.04]"
    />

    <div className="mx-auto max-w-7xl px-5 md:px-10 pt-24 md:pt-36">
      <FadeUp className="flex justify-start md:justify-end md:pr-14 mb-6">
        <a
          href={PANTHER_LINKS.questions}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="panther-official-form-cta"
          className="group flex items-center gap-4 px-7 py-5 bg-blaze text-white hover:-translate-y-0.5 hover:shadow-[0_16px_44px_rgba(200,16,46,0.45)] transition-all duration-200"
        >
          <span className="relative flex h-3 w-3 shrink-0" aria-hidden="true">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
          </span>
          <span className="flex flex-col text-left">
            <span className="flex items-center gap-3">
              <span className="font-accent uppercase tracking-[0.22em] text-sm">Formulaire officiel</span>
              <img src="/img/panther-band-white.png" alt="PANTHER" className="h-6 w-auto" />
            </span>
            <span className="mt-1 font-accent uppercase tracking-[0.28em] text-[11px] text-white/80">Réponse sous 24 h</span>
          </span>
          <ArrowUpRight className="h-5 w-5 ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </a>
      </FadeUp>
      <div className="grid sm:grid-cols-2 gap-5">
        {[
          { src: "/img/panther-gavf44.png", alt: "Maillot de football sublimé PANTHER — écusson GAVF44", caption: "Vos couleurs, votre sponsor" },
          { src: "/img/panther-team.jpg", alt: "Équipe cliente équipée de maillots sublimés PANTHER", caption: "De la maquette au terrain" },
        ].map((g, i) => (
          <FadeUp key={i} delay={i * 0.08} className="relative overflow-hidden group min-h-[300px] md:min-h-[460px] border border-black/10" data-testid={`panther-gallery-${i}`}>
            <img
              src={g.src}
              alt={g.alt}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
            <p className="absolute bottom-0 left-0 right-0 p-5 font-accent uppercase tracking-[0.25em] text-white/90 text-xs">{g.caption}</p>
          </FadeUp>
        ))}
      </div>
    </div>

    <div className="mx-auto max-w-7xl px-5 md:px-10 pt-16 pb-14 grid lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-7 relative">
        <FadeUp className="flex items-center gap-6 mb-12 relative">
          <span className="font-display text-blaze text-2xl">02</span>
          <span className="h-px w-24 bg-black/20" />
          <span className="font-accent uppercase tracking-[0.4em] text-black/50 text-sm">La brique Panther</span>
        </FadeUp>

        <FadeUp delay={0.1}>
          <PantherWordmark big dark />
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-8 max-w-lg text-black/60 text-base md:text-lg leading-relaxed font-light" data-testid="panther-description">
            Le cœur de métier PANTHER : <strong className="font-semibold text-black">maillots, shorts et kits complets sublimés</strong>,
            imprimés aux couleurs de votre club ou de votre événement.
          </p>
        </FadeUp>
        <FadeUp delay={0.25} className="mt-6 flex flex-wrap gap-2">
          {["Maillots sublimés", "Shorts & chaussettes", "Kits complets", "100 % personnalisable"].map((t) => (
            <span key={t} className="font-accent uppercase tracking-[0.18em] text-[11px] px-3 py-1.5 border border-black/20 text-black/60">
              {t}
            </span>
          ))}
        </FadeUp>

        <FadeUp delay={0.3} className="mt-12 flex flex-wrap gap-4">
          <a
            href={PANTHER_LINKS.website}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="panther-link-website"
            className="group flex items-center gap-3 px-7 py-4 bg-black text-white font-accent uppercase tracking-[0.22em] text-sm hover:bg-blaze transition-colors duration-200"
          >
            <Globe className="h-4 w-4" /> Site web
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a
            href={PANTHER_LINKS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="panther-link-facebook"
            className="flex items-center gap-3 px-7 py-4 border border-black/30 text-black font-accent uppercase tracking-[0.22em] text-sm hover:border-blaze hover:text-blaze transition-colors duration-200"
          >
            <Facebook className="h-4 w-4" /> Facebook
          </a>
          <a
            href={PANTHER_LINKS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="panther-link-instagram"
            className="flex items-center gap-3 px-7 py-4 border border-black/30 text-black font-accent uppercase tracking-[0.22em] text-sm hover:border-blaze hover:text-blaze transition-colors duration-200"
          >
            <Instagram className="h-4 w-4" /> Instagram
          </a>
        </FadeUp>
      </div>

      <FadeUp delay={0.15} className="lg:col-span-5">
        <div className="bg-white border border-black/10 p-8 md:p-12 flex flex-col items-center shadow-[0_20px_60px_rgba(0,0,0,0.08)] relative overflow-hidden">
          <img
            src="/img/panther-band.png"
            alt=""
            aria-hidden="true"
            className="h-10 md:h-12 w-auto"
          />
          <img
            src="/img/panther-head-black.png"
            alt="Logo PANTHER — tête de panthère"
            className="mt-8 h-32 md:h-44 w-auto"
            data-testid="panther-logo-card"
          />
          <p className="mt-6 font-accent uppercase tracking-[0.35em] text-black/40 text-xs">Design for you</p>
          <div className="mt-8 flex items-center gap-4">
            <a
              href={PANTHER_LINKS.website}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="panther-swatch-website"
              aria-label="Site PANTHER"
              title="Site PANTHER"
              className="w-6 h-6 bg-black border border-black/10 hover:scale-125 hover:ring-2 hover:ring-black/30 transition-all duration-200"
            />
            <a
              href={PANTHER_LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="panther-swatch-facebook"
              aria-label="Facebook PANTHER"
              title="Facebook PANTHER"
              className="w-6 h-6 bg-royal hover:scale-125 hover:ring-2 hover:ring-royal/40 transition-all duration-200"
            />
            <a
              href={PANTHER_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="panther-swatch-instagram"
              aria-label="Instagram PANTHER"
              title="Instagram PANTHER"
              className="w-6 h-6 bg-blaze hover:scale-125 hover:ring-2 hover:ring-blaze/40 transition-all duration-200"
            />
          </div>
        </div>
      </FadeUp>
      </div>

      <div className="mx-auto max-w-7xl px-5 md:px-10 pb-24 md:pb-32 space-y-5">
        <FadeUp>
          <div className="bg-ink text-white p-8 md:p-12 border border-black/10 flex flex-col md:flex-row md:items-center justify-between gap-8" data-testid="panther-kit-detail">
            <div>
              <span className="font-accent uppercase tracking-[0.4em] text-blaze text-sm">Kit détail</span>
              <h3 className="mt-4 font-display uppercase text-3xl md:text-4xl leading-[0.95]">
                Le guide des tailles du kit
              </h3>
              <p className="mt-4 text-white/60 font-light leading-relaxed max-w-md">
                Mensurations du kit maillot + short — du tout-petit au grand gabarit.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <button
                onClick={() => setKit("kids")}
                data-testid="panther-kit-kids"
                className="group flex items-center justify-between gap-3 px-6 py-4 bg-white text-ink font-accent uppercase tracking-[0.22em] text-sm hover:bg-blaze hover:text-white transition-colors duration-200"
              >
                <span className="flex items-center gap-3"><Shirt className="h-4 w-4" /> Kit détail enfant</span>
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
              <button
                onClick={() => setKit("adult")}
                data-testid="panther-kit-adult"
                className="group flex items-center justify-between gap-3 px-6 py-4 border border-white/25 text-white font-accent uppercase tracking-[0.22em] text-sm hover:border-blaze hover:text-blaze transition-colors duration-200"
              >
                <span className="flex items-center gap-3"><Shirt className="h-4 w-4" /> Kit détail adulte</span>
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </FadeUp>
      </div>

      <AnimatePresence>
        {kit && (
          <motion.div
            key="kit-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setKit(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-10"
            data-testid="panther-kit-modal"
          >
            <motion.div
              initial={{ scale: 0.94, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 12 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative flex flex-col items-center"
            >
              <button
                onClick={() => setKit(null)}
                data-testid="panther-kit-modal-close"
                aria-label="Fermer le guide des tailles"
                className="absolute -top-12 right-0 h-10 w-10 flex items-center justify-center bg-white text-ink hover:bg-blaze hover:text-white transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
              <img
                src={KIT_GUIDES[kit].src}
                alt={KIT_GUIDES[kit].alt}
                className="max-h-[78vh] w-auto border border-white/10"
                data-testid={`panther-kit-image-${kit}`}
              />
              <p className="mt-4 font-accent uppercase tracking-[0.3em] text-white/70 text-xs">
                Guide des tailles PANTHER — {KIT_GUIDES[kit].label}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
  </section>
  );
};

const QuoteTeaser = () => (
  <section className="py-24 md:py-32" data-testid="quote-teaser">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <FadeUp className="bg-panel border border-white/10 p-10 md:p-16 relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-10">
          <div className="max-w-2xl">
            <p className="font-accent uppercase tracking-[0.35em] text-blaze text-sm mb-6">Sans paiement en ligne</p>
            <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl md:text-6xl">
              Une référence en tête ?
              <br />
              <span className="text-outline">Envoyez votre demande.</span>
            </h2>
            <p className="mt-6 text-white/60 font-light max-w-xl" data-testid="quote-teaser-text">
              Parcourez le catalogue Tremblay et notez la référence du produit, ou envoyez
              directement votre demande PANTHER via son formulaire dédié — SIRET et
              référence obligatoires, nous revenons vers vous avec un devis.
            </p>
          </div>
          <div className="flex flex-col gap-4 w-fit">
            <Link
              to="/catalogue#demande"
              data-testid="quote-teaser-cta"
              className="group px-8 py-4 bg-blaze text-white font-accent uppercase tracking-[0.22em] text-sm flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_rgba(200,16,46,0.4)] transition-all duration-200"
            >
              Formulaire Tremblay
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={PANTHER_LINKS.questions}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quote-teaser-cta-panther"
              className="group px-8 py-4 border border-white/25 text-white font-accent uppercase tracking-[0.22em] text-sm flex items-center gap-3 hover:border-blaze hover:text-blaze transition-colors duration-200 w-fit"
            >
              Formulaire PANTHER
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

const PantherFormSection = () => (
  <section id="demande-panther" className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden bg-[#0A0A0A]" data-testid="panther-form-section">
    <img
      src="/img/panther-head-white.png"
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-16 -right-16 h-[420px] opacity-[0.05]"
    />
    <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-12 gap-14">
      <FadeUp className="lg:col-span-5">
        <PantherWordmark />
        <p className="font-accent uppercase tracking-[0.35em] text-blaze text-sm mt-10">Formulaire PANTHER</p>
        <h2 className="mt-6 font-display uppercase leading-[0.9] tracking-tight text-4xl md:text-6xl">
          Votre demande
          <br />
          <span className="text-outline">PANTHER.</span>
        </h2>
        <p className="mt-8 text-white/60 leading-relaxed font-light max-w-md" data-testid="panther-form-description">
          Un produit PANTHER en vue ? Envoyez votre demande directement.
        </p>
        <a
          href={PANTHER_LINKS.questions}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="panther-form-official-link"
          className="mt-8 w-fit flex items-center gap-3 border border-white/25 px-5 py-3 text-xs font-accent uppercase tracking-[0.18em] text-white/70 hover:text-blaze hover:border-blaze transition-colors duration-200"
        >
          Formulaire officiel PANTHER (réponse sous 24 h) ↗
        </a>
      </FadeUp>
      <FadeUp delay={0.15} className="lg:col-span-7">
        <QuoteForm brand="PANTHER" />
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="bg-ink text-white min-h-screen">
      <Navbar />
      <main>
        <Hero />
        <RibbonMarquee
          items={[
            "Partenaire de toutes vos victoires",
            "Tremblay — depuis plus de 50 ans",
            "Panther Design For You",
            "Catalogue 2026",
          ]}
        />
        <TremblayBrique />
        <PantherBrique />
        <PantherFormSection />
        <QuoteTeaser />
      </main>
      <Footer />
    </div>
  );
}
